import { useState, useEffect, useId, useRef } from 'react'
import { motion } from 'framer-motion'
import { ArrowRight } from 'lucide-react'
import { Button } from '@/components/ui'
import type { IntakeFormData } from '@/hooks/useIntakeWizard'

interface ContactStepProps {
  formData: IntakeFormData
  updateField: <K extends keyof IntakeFormData>(field: K, value: IntakeFormData[K]) => void
  onNext: () => void
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export function ContactStep({ formData, updateField, onNext }: ContactStepProps) {
  const [errors, setErrors] = useState<{ name?: string; email?: string }>({})
  const nameInputRef = useRef<HTMLInputElement>(null)
  const nameId = useId()
  const emailId = useId()

  useEffect(() => {
    // Focus the first empty field
    if (!formData.name) {
      nameInputRef.current?.focus()
    }
  }, [formData.name])

  const validate = (): boolean => {
    const next: { name?: string; email?: string } = {}
    if (!formData.name.trim()) next.name = 'Please enter your name'
    if (!formData.email.trim()) {
      next.email = 'Please enter your email'
    } else if (!EMAIL_REGEX.test(formData.email)) {
      next.email = 'Please enter a valid email address'
    }
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (validate()) onNext()
  }

  return (
    <form onSubmit={handleSubmit} className="mx-auto max-w-xl space-y-8">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-3">
        <h2 className="text-foreground text-3xl font-bold md:text-4xl">Let's start with the basics</h2>
        <p className="text-muted-foreground text-lg">How should we reach you?</p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="space-y-6"
      >
        <div>
          <label htmlFor={nameId} className="text-muted-foreground mb-1 block text-sm font-medium">
            Your name
          </label>
          <input
            id={nameId}
            ref={nameInputRef}
            type="text"
            placeholder="Jane Smith"
            value={formData.name}
            onChange={(e) => {
              updateField('name', e.target.value)
              if (errors.name) setErrors((prev) => ({ ...prev, name: undefined }))
            }}
            className="border-border focus:border-primary w-full border-b-2 bg-transparent px-4 py-4 text-xl transition-colors focus:outline-none"
          />
          {errors.name && <p className="mt-2 text-sm text-red-500">{errors.name}</p>}
        </div>

        <div>
          <label htmlFor={emailId} className="text-muted-foreground mb-1 block text-sm font-medium">
            Email
          </label>
          <input
            id={emailId}
            type="email"
            placeholder="jane@example.com"
            value={formData.email}
            onChange={(e) => {
              updateField('email', e.target.value)
              if (errors.email) setErrors((prev) => ({ ...prev, email: undefined }))
            }}
            className="border-border focus:border-primary w-full border-b-2 bg-transparent px-4 py-4 text-xl transition-colors focus:outline-none"
          />
          {errors.email && <p className="mt-2 text-sm text-red-500">{errors.email}</p>}
        </div>
      </motion.div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}>
        <Button type="submit" size="lg">
          Continue
          <ArrowRight className="ml-2 h-5 w-5" />
        </Button>
      </motion.div>

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="text-muted-foreground text-sm"
      >
        Step 1 of 5
      </motion.p>
    </form>
  )
}
